import * as React from 'react';
import { useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { useQuery } from 'react-query';
import apiClient from '../../request/http-common';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { Button, Chip, Box, TextField, InputAdornment, IconButton, Menu, MenuItem } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import { Link as RouterLink } from 'react-router-dom';

export default function ListApplications() {
    const [applications, setApplications] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [anchorEl, setAnchorEl] = useState(null);
    
    const { isLoading: isLoadingApplications, refetch: getApplications } = useQuery(
        ["load-applications", searchTerm, statusFilter],
        async () => {
            const params = new URLSearchParams();
            if (searchTerm) params.append('search', searchTerm);
            if (statusFilter) params.append('status', statusFilter);
            
            return await apiClient.get(`/api/applications?${params.toString()}`);
        },
        {
            enabled: false,
            onSuccess: (res) => {
                if (res.data.status === 'success') {
                    setApplications(res.data.data.data);
                    const event2 = new Event('processed');
                    window.dispatchEvent(event2);
                }
            },
            onError: (err) => {
                console.log(err.response?.data || err);
                const event2 = new Event('processed');
                window.dispatchEvent(event2);
            },
        }
    );
    
    useEffect(() => {
        getApplications();
        if (isLoadingApplications) {
            const event3 = new Event('processing');
            window.dispatchEvent(event3);
        }
    }, [searchTerm, statusFilter]);

    const getDate = (date) => new Date(date).toDateString();
    
    const getStatusColor = (status) => {
        switch (status) {
            case 'pending': return 'warning';
            case 'approved': return 'success';
            case 'rejected': return 'error';
            case 'processing': return 'info';
            case 'completed': return 'primary';
            case 'migrated_to_user': return 'success';
            default: return 'default';
        }
    };

    const handleFilterClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleFilterClose = () => {
        setAnchorEl(null);
    };

    const handleStatusFilter = (status) => {
        setStatusFilter(status);
        handleFilterClose();
    };

    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
    };

    return (
        <div style={{marginTop: '75px'}}>
            <Container>
                <Typography mt={4} mb={2} sx={{ fontWeight: 'bold', fontSize: 20, textAlign: 'center', textTransform: 'uppercase' }} gutterBottom variant="h6" component="div">
                    {isLoadingApplications ? 'Loading Applications...' : 'Basic Applications Management'}
                </Typography>
                
                {/* Search and Filter Controls */}
                <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
                    <TextField
                        size="small"
                        placeholder="Search by name or email..."
                        value={searchTerm}
                        onChange={handleSearch}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ flexGrow: 1, maxWidth: 400 }}
                    />
                    
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {statusFilter && (
                            <Chip 
                                label={`Status: ${statusFilter}`} 
                                onDelete={() => setStatusFilter('')}
                                color={getStatusColor(statusFilter)}
                                variant="outlined"
                            />
                        )}
                        
                        <IconButton onClick={handleFilterClick}>
                            <FilterListIcon />
                        </IconButton>
                        
                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleFilterClose}
                        >
                            <MenuItem onClick={() => handleStatusFilter('')}>All Status</MenuItem>
                            <MenuItem onClick={() => handleStatusFilter('pending')}>Pending</MenuItem>
                            <MenuItem onClick={() => handleStatusFilter('processing')}>Processing</MenuItem>
                            <MenuItem onClick={() => handleStatusFilter('approved')}>Approved</MenuItem>
                            <MenuItem onClick={() => handleStatusFilter('rejected')}>Rejected</MenuItem>
                            <MenuItem onClick={() => handleStatusFilter('completed')}>Completed</MenuItem>
                            <MenuItem onClick={() => handleStatusFilter('migrated_to_user')}>Login Access Granted</MenuItem>
                        </Menu>
                    </Box>
                </Box>

                <TableContainer component={Paper}>
                    {applications !== null && (
                        <Table sx={{ minWidth: '100%' }} size="small" aria-label="applications table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell align="right">Email</TableCell>
                                    <TableCell align="right">Phone</TableCell>
                                    <TableCell align="right">Nationality</TableCell>
                                    <TableCell align="right">Country</TableCell>
                                    <TableCell align="right">Age</TableCell>
                                    <TableCell align="right">Status</TableCell>
                                    <TableCell align="right">Submitted</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {applications.map((application) => {
                                    const age = new Date().getFullYear() - new Date(application.date_of_birth).getFullYear();
                                    return (
                                        <TableRow
                                            key={application.id}
                                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                        >
                                            <TableCell component="th" scope="row">
                                                <Box sx={{ fontWeight: 'medium' }}>
                                                    {application.name}
                                                </Box>
                                                <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                                    ID: {application.id}
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right">{application.email}</TableCell>
                                            <TableCell align="right">{application.phone}</TableCell>
                                            <TableCell align="right">{application.nationality}</TableCell>
                                            <TableCell align="right">{application.country_of_residence}</TableCell>
                                            <TableCell align="right">{age} years</TableCell>
                                            <TableCell align="right">
                                                <Chip 
                                                    label={application.status === 'migrated_to_user' ? 'Login Access Granted' : application.status} 
                                                    color={getStatusColor(application.status)}
                                                    size="small"
                                                    sx={{ textTransform: 'capitalize' }}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Box sx={{ fontSize: '0.875rem' }}>
                                                    {getDate(application.created_at)}
                                                </Box>
                                                <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                                    {application.email_sent_at ? 'Email sent' : 'Email pending'}
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Button 
                                                    component={RouterLink} 
                                                    to={`/view-application/${application.id}`} 
                                                    startIcon={<VisibilityIcon />}
                                                    size="small"
                                                    variant="outlined"
                                                >
                                                    View
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                    
                    {applications && applications.length === 0 && (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography variant="body1" color="text.secondary">
                                No applications found matching your criteria.
                            </Typography>
                        </Box>
                    )}
                </TableContainer>
            </Container>
        </div>
    );
} 